import React, { useEffect, useRef } from "react";
import { ArrowRight } from "lucide-react";
import { Callout, Checkbox } from "../../ui";
import SkippedTable from "./SkippedTable";
import { arrivals, batchStart, groupPreview } from "./addToBatch";

const plural = (n, one, many) => (n === 1 ? one : many);

/**
 * TransferReview — the question "Add to batch" exists to ask: these students
 * already study the course in another batch; transfer them here?
 *
 * Each one is a tick, all ticked to start with (the admin chose these students
 * for this batch). Beside the question: who else is joining and how, who will
 * not be added and why, and whether the batch has room for the answer.
 *
 * Props:
 *   preview      — POST /students/add-to-batch/preview
 *   transferIds  — Set of student ids ticked for transfer
 *   onToggle     — (studentId) => void
 *   onToggleAll  — (checked) => void
 *   feeNote      — "at ₹12,000" for new enrolments, or "" to say nothing of fees
 *   terms        — useAudienceTerm()
 */
const TransferReview = ({ preview, transferIds, onToggle, onToggleAll, feeNote = "", terms }) => {
  const groups = groupPreview(preview.students);
  const { batch, seats } = preview;
  const arrival = arrivals(preview, transferIds);
  const moves = groups.move;
  const ticked = moves.filter((r) => transferIds.has(r.studentId)).length;
  const noun = (n) => plural(n, terms.lower, terms.lowerPlural);

  const allRef = useRef(null);
  useEffect(() => {
    if (allRef.current) allRef.current.indeterminate = ticked > 0 && ticked < moves.length;
  }, [ticked, moves.length]);

  const joining = [
    groups.add.length > 0 && {
      key: "add",
      title: `${groups.add.length} new ${plural(groups.add.length, "enrolment", "enrolments")}`,
      detail: `Enrolled in ${batch.course.name}${feeNote ? ` ${feeNote}` : ""} and placed in this batch.`,
    },
    groups.assign.length > 0 && {
      key: "assign",
      title: `${groups.assign.length} enrolled in the course without a batch`,
      detail: "Placed in this batch.",
    },
    groups.rejoin.length > 0 && {
      key: "rejoin",
      title: `${groups.rejoin.length} rejoining`,
      detail: "They had left the course. Their enrolment is reopened here, with its fee and payments.",
    },
  ].filter(Boolean);

  const target = [batch.course.name, batchStart(batch), batch.branch?.name].filter(Boolean).join(" · ");

  return (
    <div className="tw-flex tw-flex-col tw-gap-5">
      <p className="tw-m-0 tw-text-sm tw-text-content-muted">
        Adding to <span className="tw-font-semibold tw-text-content">{batch.name}</span>
        {target ? ` — ${target}` : ""}
      </p>

      {moves.length > 0 && (
        <section aria-labelledby="atb-move-title" className="tw-flex tw-flex-col tw-gap-3">
          <div>
            <h3 id="atb-move-title" className="tw-m-0 tw-text-sm tw-font-semibold tw-leading-snug tw-text-content">
              {moves.length} {noun(moves.length)} already {plural(moves.length, "studies", "study")} {batch.course.name} in another batch
            </h3>
            <p className="tw-m-0 tw-mt-1 tw-text-[0.8125rem] tw-leading-normal tw-text-content-muted">
              Transfer them to {batch.name}? A transferred {terms.lower} leaves their
              current batch. Their fee and payments are kept, and attendance already marked stays with the old
              batch. Untick anyone who should stay where they are.
            </p>
          </div>

          {moves.length > 1 && (
            <Checkbox
              ref={allRef}
              checked={ticked === moves.length}
              onChange={(e) => onToggleAll(e.target.checked)}
              label={`Transfer all ${moves.length}`}
              labelClassName="tw-font-semibold"
            />
          )}

          <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-rounded-md sm:tw-max-h-64 sm:tw-overflow-y-auto tw-border tw-border-solid tw-border-line tw-p-0">
            {moves.map((row) => (
              <li
                key={row.studentId}
                className="tw-border-0 tw-border-b tw-border-solid tw-border-line tw-px-3 tw-py-2.5 last:tw-border-b-0"
              >
                <Checkbox
                  checked={transferIds.has(row.studentId)}
                  onChange={() => onToggle(row.studentId)}
                  label={row.inactive ? `${row.name} (inactive)` : row.name}
                  description={(
                    <span className="tw-inline-flex tw-flex-wrap tw-items-center tw-gap-1.5">
                      <span>{row.from?.name || "Another batch"}</span>
                      <ArrowRight size={12} aria-hidden="true" />
                      <span className="tw-sr-only">to</span>
                      <span className="tw-font-medium tw-text-content">{batch.name}</span>
                    </span>
                  )}
                />
              </li>
            ))}
          </ul>
        </section>
      )}

      {joining.length > 0 && (
        <section aria-labelledby="atb-joining-title" className="tw-flex tw-flex-col tw-gap-2">
          <h3 id="atb-joining-title" className="tw-m-0 tw-text-sm tw-font-semibold tw-text-content">
            {moves.length ? "Also joining" : "Joining"}
          </h3>
          <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-1.5 tw-p-0">
            {joining.map((item) => (
              <li key={item.key} className="tw-text-[0.8125rem] tw-leading-normal tw-text-content-muted">
                <span className="tw-font-semibold tw-text-content">{item.title}.</span> {item.detail}
              </li>
            ))}
          </ul>
        </section>
      )}

      {groups.skip.length > 0 && (
        <details className="tw-flex tw-flex-col tw-gap-2">
          <summary className="tw-cursor-pointer tw-text-[0.8125rem] tw-font-medium tw-text-content">
            {groups.skip.length} {noun(groups.skip.length)} won’t be added — see why
          </summary>
          <div className="tw-mt-2">
            <SkippedTable rows={groups.skip} terms={terms} />
          </div>
        </details>
      )}

      {!arrival.fits ? (
        <Callout tone="danger" role="alert">
          {batch.name} has {seats.left} {plural(seats.left, "seat", "seats")} left and this adds {arrival.arriving}.{" "}
          {moves.length ? "Untick some transfers, or raise the batch’s capacity." : "Raise the batch’s capacity, or choose another batch."}
        </Callout>
      ) : (
        <p className="tw-m-0 tw-text-xs tw-text-content-subtle" aria-live="polite">
          {seats.limit
            ? `${arrival.after} of ${seats.limit} seats taken after this.`
            : `${arrival.after} ${noun(arrival.after)} in the batch after this.`}
        </p>
      )}
    </div>
  );
};

export default TransferReview;
